import React, { useEffect, useState } from 'react';
import { parentApi } from '../../api/client';
import { useLocation } from 'react-router-dom';
import Spinner from '../../components/common/Spinner';
import { FaCalendarCheck, FaChartBar, FaExclamationTriangle, FaMoneyBill } from 'react-icons/fa';

export default function ChildProgress() {
  const location    = useLocation();
  const [children,  setChildren]  = useState([]);
  const [selected,  setSelected]  = useState('');
  const [progress,  setProgress]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [fetching,  setFetching]  = useState(true);

  useEffect(() => {
    parentApi.getChildren()
      .then(r => {
        const list = r.data?.data || [];
        setChildren(list);
        // Pre-select child passed via navigation state, else first child
        const navId = location.state?.learnerId;
        const match = navId && list.find(c => String(c.user_id) === String(navId));
        if (match)       setSelected(match.user_id);
        else if (list.length > 0) setSelected(list[0].user_id);
      })
      .finally(() => setFetching(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    parentApi.getProgress(selected)
      .then(r => setProgress(r.data.data))
      .finally(() => setLoading(false));
  }, [selected]);

  const gradeColor = (avg) => avg >= 75 ? '#10b981' : avg >= 50 ? '#f59e0b' : '#ef4444';

  if (fetching) return <Spinner />;

  const att = progress?.attendance;
  const attPct = att?.total > 0 ? Math.round((att.present / att.total) * 100) : null;

  return (
    <div>
      <div className="page-header"><h2>Children's Progress</h2></div>

      {children.length > 1 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {children.map(c => (
            <button key={c.user_id} onClick={() => setSelected(c.user_id)}
              className="sms-btn" style={{ background: String(selected) === String(c.user_id) ? '#0d6efd' : '#fff', color: String(selected) === String(c.user_id) ? '#fff' : '#374151', border: '1px solid #e5e7eb' }}>
              {c.first_name} {c.surname}
            </button>
          ))}
        </div>
      )}

      {loading ? <Spinner /> : progress && (
        <>
          {/* Attendance summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 24 }}>
            {[
              ['Total Days', att?.total, '#6b7280', FaCalendarCheck],
              ['Present',    att?.present, '#10b981', FaCalendarCheck],
              ['Absent',     att?.absent,  '#ef4444', FaCalendarCheck],
              ['Late',       att?.late,    '#f59e0b', FaCalendarCheck],
              ['Attendance', attPct !== null ? `${attPct}%` : '—', attPct >= 75 ? '#10b981' : '#ef4444', FaCalendarCheck],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background: '#fff', borderRadius: 12, padding: 14, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: `1px solid ${c}22` }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: c }}>{v}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Marks */}
          <div className="sms-card" style={{ marginBottom: 20 }}>
            <h5 style={{ fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><FaChartBar color="#0d6efd" /> Academic Performance</h5>
            {progress.marks?.length > 0 ? (
              <table className="sms-table">
                <thead><tr><th>Subject</th><th>Assessment</th><th>Type</th><th>Score</th><th>Total</th><th>%</th></tr></thead>
                <tbody>
                  {progress.marks.map((m, i) => {
                    const pct = m.score !== null && m.total_marks > 0 ? Math.round((m.score / m.total_marks) * 100) : null;
                    return (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{m.subject_name}</td>
                        <td>{m.assessment}</td>
                        <td style={{ textTransform: 'capitalize', fontSize: '0.82rem' }}>{m.type}</td>
                        <td style={{ fontWeight: 600 }}>{m.score ?? '—'}</td>
                        <td>{m.total_marks}</td>
                        <td style={{ fontWeight: 600, color: pct !== null ? gradeColor(pct) : '#9ca3af' }}>{pct !== null ? `${pct}%` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : <p style={{ color: '#9ca3af' }}>No marks recorded</p>}
          </div>

          {/* Behaviour */}
          {progress.behaviour?.length > 0 && (
            <div className="sms-card" style={{ marginBottom: 20 }}>
              <h5 style={{ fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><FaExclamationTriangle color="#f59e0b" /> Behaviour Records</h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {progress.behaviour.map((b, i) => (
                  <div key={i} style={{ padding: '10px 14px', borderRadius: 8, background: b.type === 'positive' ? '#f0fdf4' : b.type === 'negative' ? '#fef2f2' : '#f9fafb', borderLeft: `3px solid ${b.type === 'positive' ? '#10b981' : b.type === 'negative' ? '#ef4444' : '#6b7280'}` }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', textTransform: 'capitalize' }}>{b.type}</span>
                      {b.category && <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>· {b.category}</span>}
                    </div>
                    <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#374151' }}>{b.description}</p>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{b.incident_date} · {b.recorded_by}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outstanding fees */}
          {progress.outstanding_fees?.length > 0 && (
            <div className="sms-card">
              <h5 style={{ fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><FaMoneyBill color="#ef4444" /> Outstanding Fees</h5>
              <table className="sms-table">
                <thead><tr><th>Fee</th><th>Amount</th><th>Paid</th><th>Outstanding</th><th>Due Date</th></tr></thead>
                <tbody>
                  {progress.outstanding_fees.map((f, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{f.fee_name}</td>
                      <td>R {parseFloat(f.amount).toFixed(2)}</td>
                      <td style={{ color: '#10b981' }}>R {parseFloat(f.paid).toFixed(2)}</td>
                      <td style={{ color: '#ef4444', fontWeight: 700 }}>R {parseFloat(f.outstanding).toFixed(2)}</td>
                      <td style={{ color: new Date(f.due_date) < new Date() ? '#ef4444' : '#6b7280' }}>{f.due_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
